import React, { useState, useRef, useCallback, useEffect } from "react";

// Helper to get clientX for both mouse and touch events
const getClientX = (e) => {
  return e.touches ? e.touches[0].clientX : e.clientX;
};

const VideoSlider = ({ videoIds }) => {
  const sliderRef = useRef(null);
  const [isDraggingUI, setIsDraggingUI] = useState(false);
  const dragStartInfo = useRef({ x: 0, scrollLeft: 0 });
  const isActiveDragRef = useRef(false);

  const handleDragMoveGlobal = useCallback((e) => {
    if (!isActiveDragRef.current || !sliderRef.current) return;

    if (e.cancelable) e.preventDefault();

    const currentX = getClientX(e);
    const walk = currentX - dragStartInfo.current.x;
    sliderRef.current.scrollLeft = dragStartInfo.current.scrollLeft - walk;
  }, []);

  const handleDragEndGlobal = useCallback(() => {
    if (!isActiveDragRef.current) return;
    isActiveDragRef.current = false;
    setIsDraggingUI(false);

    if (sliderRef.current) {
      sliderRef.current.classList.remove("cursor-grabbing");
      sliderRef.current.classList.add("cursor-grab");
    }

    window.removeEventListener("mousemove", handleDragMoveGlobal);
    window.removeEventListener("touchmove", handleDragMoveGlobal);
    window.removeEventListener("mouseup", handleDragEndGlobal);
    window.removeEventListener("touchend", handleDragEndGlobal);
  }, [setIsDraggingUI, handleDragMoveGlobal]);

  const handleDragStart = useCallback(
    (e) => {
      if (e.type === "mousedown" && e.button !== 0) return;
      if (!sliderRef.current) return;

      isActiveDragRef.current = true;
      setIsDraggingUI(true); // Update UI state

      dragStartInfo.current = {
        x: getClientX(e),
        scrollLeft: sliderRef.current.scrollLeft,
      };

      if (sliderRef.current) {
        sliderRef.current.classList.add("cursor-grabbing");
        sliderRef.current.classList.remove("cursor-grab");
      }

      window.addEventListener("mousemove", handleDragMoveGlobal);
      window.addEventListener("touchmove", handleDragMoveGlobal, {
        passive: false,
      });
      window.addEventListener("mouseup", handleDragEndGlobal);
      window.addEventListener("touchend", handleDragEndGlobal);
    },
    [setIsDraggingUI, handleDragMoveGlobal, handleDragEndGlobal]
  );

  useEffect(() => {
    return () => {
      if (isActiveDragRef.current) {
        window.removeEventListener("mousemove", handleDragMoveGlobal);
        window.removeEventListener("touchmove", handleDragMoveGlobal);
        window.removeEventListener("mouseup", handleDragEndGlobal);
        window.removeEventListener("touchend", handleDragEndGlobal);
        isActiveDragRef.current = false;
      }
    };
  }, [handleDragMoveGlobal, handleDragEndGlobal]);

  if (!videoIds || videoIds.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 bg-gray-800 rounded-lg">
        No videos to display. Please provide a 'videoIds' prop.
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-x-hidden select-none bg-gray-900 p-4 rounded-xl">
      <div className="text-center text-gray-400 text-sm mb-3 animate-pulse">
        &larr; Drag to Explore &rarr;
      </div>
      <div
        ref={sliderRef}
        className="flex h-full space-x-4 p-1 cursor-grab scrollbar-hide rounded-lg overflow-x-hidden"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        {videoIds.map((videoId, index) => (
          <div
            key={`${videoId}-${index}`}
            className="flex-shrink-0 w-[80vw] sm:w-[70vw] md:w-[480px] lg:w-[400px] aspect-video bg-gray-800 rounded-lg shadow-xl overflow-hidden"
            style={{ pointerEvents: isDraggingUI ? "none" : "auto" }}
          >
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${videoId}`}
              title={`YouTube video player ${index + 1}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="block" // Ensures iframe takes full space of parent
            ></iframe>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoSlider;
